import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SaveIcon from "@mui/icons-material/Save";
import StarIcon from "@mui/icons-material/Star";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch } from "../../../app/hooks";
import { LoadingContainer } from "../../../app/LoadingContainer";
import { ErrorContainer } from "../../../app/ErrorContainer";
import { APIError } from "../../../app/appTypes";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { PackageAccess, packageAccessLevels, packageLabel, RolePermission } from "../../permissions/permissionsSlice";
import {
    CompanyRole,
    createCompanyRole,
    deleteCompanyRole,
    loadCompanyRoles,
    setDefaultCompanyRole,
    updateCompanyRole,
} from "./companyRolesApi";

type Draft = {
    id: string | null
    name: string
    description: string
    permissions: RolePermission[]
}


const toDraft = (role: CompanyRole): Draft => ({
    id: role.id,
    name: role.name,
    description: role.description ?? "",
    permissions: role.permissions.map(p => ({ ...p })),
})

const samePermissions = (a: RolePermission[], b: RolePermission[]) =>
    a.length === b.length && a.every(p => b.find(x => x.package === p.package)?.access === p.access)

const accessL10n: Record<PackageAccess, [string, string]> = {
    None: ["package-access-none", "_Brak"],
    Read: ["package-access-read", "_Odczyt"],
    ReadWrite: ["package-access-read-write", "_Odczyt i zapis"],
}

const accessIcon: Record<PackageAccess, React.ReactElement> = {
    None: <BlockIcon fontSize="small" sx={{ color: "text.disabled" }} />,
    Read: <VisibilityIcon fontSize="small" color="primary" />,
    ReadWrite: <EditNoteIcon fontSize="small" color="success" />,
}

type ModuleGroup = { code: string, name: string, permissions: RolePermission[] }

/** Packages grouped by module, modules in the order the server lists them. */
const groupByModule = (permissions: RolePermission[]): ModuleGroup[] => {
    const groups: ModuleGroup[] = []
    for (const permission of permissions) {
        const code = permission.moduleCode ?? ""
        let group = groups.find(g => g.code === code)
        if (!group) {
            group = { code, name: permission.moduleName ?? code, permissions: [] }
            groups.push(group)
        }
        group.permissions.push(permission)
    }
    return groups
}

const errorKey = (error: APIError | undefined) =>
    error?.kind === "http" && error.problem?.title ? error.problem.title : "error"

const CompanyRoles: React.FC = () => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()

    const [roles, setRoles] = useState<CompanyRole[] | null>(null)
    const [loadError, setLoadError] = useState<APIError | null>(null)
    const [draft, setDraft] = useState<Draft | null>(null)
    const [isSaving, setIsSaving] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    // Role that was open before "New role" / "Duplicate", so cancelling an unsaved role returns to it.
    const [returnToId, setReturnToId] = useState<string | null>(null)

    const reload = useCallback(async (selectId?: string | null) => {
        try {
            const loaded = await dispatch(loadCompanyRoles()).unwrap()
            setRoles(loaded)
            const selected = loaded.find(r => r.id === selectId) ?? loaded[0]
            setDraft(selected ? toDraft(selected) : null)
        } catch (error) {
            setLoadError(error as APIError)
        }
    }, [dispatch])

    useEffect(() => {
        reload()
    }, [reload])

    const savedRole = useMemo(
        () => roles?.find(r => r.id === draft?.id) ?? null,
        [roles, draft?.id]
    )

    const isDirty = useMemo(() => {
        if (!draft) return false
        if (!savedRole) return true
        return draft.name !== savedRole.name
            || draft.description !== (savedRole.description ?? "")
            || !samePermissions(draft.permissions, savedRole.permissions)
    }, [draft, savedRole])

    const confirmDiscard = () =>
        !isDirty || window.confirm(l10n.getString("company-roles-discard-changes", null, "_Odrzucić niezapisane zmiany?"))

    const handleSelect = (role: CompanyRole) => {
        if (role.id === draft?.id || !confirmDiscard()) return
        setDraft(toDraft(role))
    }

    const handleNew = () => {
        if (!roles || !confirmDiscard()) return
        const template = roles.find(r => r.isDefault) ?? roles[0]
        if (draft?.id) setReturnToId(draft.id)
        setDraft({
            id: null,
            name: "",
            description: "",
            permissions: (template?.permissions ?? []).map(p => ({ ...p, access: "None" })),
        })
    }

    const handleDuplicate = () => {
        if (!draft || !confirmDiscard()) return
        const source = savedRole ? toDraft(savedRole) : draft
        if (draft.id) setReturnToId(draft.id)
        setDraft({
            ...source,
            id: null,
            name: `${source.name} ${l10n.getString("company-roles-copy-suffix", null, "(kopia)")}`,
        })
    }

    const handleCancel = () => {
        if (savedRole) {
            setDraft(toDraft(savedRole))
            return
        }
        if (!roles) return
        const back = roles.find(r => r.id === returnToId) ?? roles.find(r => r.isDefault) ?? roles[0]
        setDraft(back ? toDraft(back) : null)
    }

    const setAccess = (pkg: string, access: PackageAccess) =>
        setDraft(d => d && { ...d, permissions: d.permissions.map(p => p.package === pkg ? { ...p, access } : p) })

    const setAllAccess = (access: PackageAccess, moduleCode?: string) =>
        setDraft(d => d && {
            ...d,
            permissions: d.permissions.map(p => moduleCode === undefined || p.moduleCode === moduleCode ? { ...p, access } : p),
        })

    const accessLabel = (level: PackageAccess) => l10n.getString(accessL10n[level][0], null, accessL10n[level][1])

    const handleSave = async () => {
        if (!draft) return
        if (!draft.name.trim()) {
            dispatch(showError("error-company-role-name-required"))
            return
        }

        setIsSaving(true)
        try {
            const data = { name: draft.name.trim(), description: draft.description.trim() || null, permissions: draft.permissions }
            const saved = draft.id
                ? await dispatch(updateCompanyRole({ id: draft.id, data })).unwrap()
                : await dispatch(createCompanyRole(data)).unwrap()
            dispatch(showSuccess("saved"))
            await reload(saved.id)
        } catch (error) {
            dispatch(showError(errorKey(error as APIError)))
        } finally {
            setIsSaving(false)
        }
    }

    const handleSetDefault = async () => {
        if (!draft?.id) return
        try {
            await dispatch(setDefaultCompanyRole(draft.id)).unwrap()
            dispatch(showSuccess("saved"))
            await reload(draft.id)
        } catch (error) {
            dispatch(showError(errorKey(error as APIError)))
        }
    }

    const handleDelete = async () => {
        setConfirmDelete(false)
        if (!draft?.id) return
        try {
            await dispatch(deleteCompanyRole(draft.id)).unwrap()
            dispatch(showSuccess("deleted"))
            await reload()
        } catch (error) {
            dispatch(showError(errorKey(error as APIError)))
        }
    }

    if (loadError) return <ErrorContainer error={loadError} />
    if (!roles) return <LoadingContainer />

    const canDelete = !!savedRole && !savedRole.isDefault && savedRole.userCount === 0

    return (
        <Box display="flex" gap={3} sx={{ p: 2 }}>
            <Paper sx={{ p: 2, width: 300, flexShrink: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    <Localized id="company-roles">_Role w firmie</Localized>
                </Typography>
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                    <Localized id="company-roles-hint">_Rola określa uprawnienia zwykłych użytkowników. Administratorzy mają pełny dostęp.</Localized>
                </Typography>
                <List dense sx={{ maxHeight: "calc(100vh - 330px)", overflow: "auto" }}>
                    {roles.map(role => (
                        <ListItemButton
                            key={role.id}
                            selected={role.id === draft?.id}
                            onClick={() => handleSelect(role)}
                            sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                            <ListItemText
                                primary={role.name}
                                secondary={l10n.getString("company-roles-user-count", { count: role.userCount }, `${role.userCount}`)}
                            />
                            {role.isDefault && (
                                <Chip size="small" label={l10n.getString("company-roles-default", null, "_Domyślna")} />
                            )}
                        </ListItemButton>
                    ))}
                    {draft && !draft.id && (
                        <ListItemButton selected sx={{ borderRadius: 1, mb: 0.5 }}>
                            <ListItemText
                                primary={draft.name || l10n.getString("company-roles-new", null, "_Nowa rola")}
                                secondary={l10n.getString("company-roles-unsaved", null, "_Niezapisana")}
                            />
                        </ListItemButton>
                    )}
                </List>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={1}>
                    <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleNew}>
                        <Localized id="company-roles-new">_Nowa rola</Localized>
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={handleDuplicate} disabled={!draft}>
                        <Localized id="company-roles-duplicate">_Duplikuj</Localized>
                    </Button>
                </Stack>
            </Paper>

            {draft && (
                <Paper sx={{ p: 2, flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            required
                            label={l10n.getString("company-roles-name", null, "_Nazwa")}
                            value={draft.name}
                            inputProps={{ maxLength: 100 }}
                            onChange={e => setDraft({ ...draft, name: e.target.value })}
                            sx={{ minWidth: 260 }}
                        />
                        <TextField
                            fullWidth
                            label={l10n.getString("company-roles-description", null, "_Opis")}
                            value={draft.description}
                            inputProps={{ maxLength: 500 }}
                            onChange={e => setDraft({ ...draft, description: e.target.value })}
                        />
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mr: 1 }}>
                            <Localized id="company-roles-permissions">_Uprawnienia</Localized>
                        </Typography>
                        <Box flex={1} />
                        <Typography variant="caption" color="text.secondary">
                            <Localized id="company-roles-set-all">_Ustaw wszystkie:</Localized>
                        </Typography>
                        {packageAccessLevels.map(level => (
                            <Button key={level} size="small" variant="outlined" startIcon={accessIcon[level]} onClick={() => setAllAccess(level)} disabled={draft.permissions.length === 0}>
                                {accessLabel(level)}
                            </Button>
                        ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                        <Localized id="company-roles-packages-hint">_Widoczne są tylko pakiety aktywne dla firmy.</Localized>
                    </Typography>

                    {draft.permissions.length === 0 ? (
                        <Alert severity="info">
                            <Localized id="company-roles-no-packages">_Firma nie ma aktywnych pakietów.</Localized>
                        </Alert>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell><Localized id="company-roles-package">_Pakiet</Localized></TableCell>
                                    <TableCell align="right"><Localized id="company-roles-access">_Dostęp</Localized></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupByModule(draft.permissions).map(group => (
                                    <React.Fragment key={group.code}>
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell sx={{ py: 0.5 }}>
                                                <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 2 }}>
                                                    {group.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 0.5 }}>
                                                {packageAccessLevels.map(level => (
                                                    <Button key={level} size="small" startIcon={accessIcon[level]} sx={{ textTransform: "none" }} onClick={() => setAllAccess(level, group.code)}>
                                                        {accessLabel(level)}
                                                    </Button>
                                                ))}
                                            </TableCell>
                                        </TableRow>
                                        {[...group.permissions]
                                            .sort((a, b) => packageLabel(l10n, a.package).localeCompare(packageLabel(l10n, b.package)))
                                            .map(permission => (
                                                <TableRow key={permission.package} hover>
                                                    <TableCell sx={{ pl: 4 }}>{packageLabel(l10n, permission.package)}</TableCell>
                                                    <TableCell align="right">
                                                        <Select
                                                            size="small"
                                                            value={permission.access}
                                                            onChange={e => setAccess(permission.package, e.target.value as PackageAccess)}
                                                            renderValue={level => (
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    {accessIcon[level]}
                                                                    <span>{accessLabel(level)}</span>
                                                                </Stack>
                                                            )}
                                                            sx={{ minWidth: 190, textAlign: "left" }}
                                                        >
                                                            {packageAccessLevels.map(level => (
                                                                <MenuItem key={level} value={level}>
                                                                    <ListItemIcon>{accessIcon[level]}</ListItemIcon>
                                                                    <ListItemText>{accessLabel(level)}</ListItemText>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button variant="contained" color="secondary" startIcon={<SaveIcon />} onClick={handleSave} disabled={isSaving || !isDirty}>
                            <Localized id="save">Zapisz</Localized>
                        </Button>
                        {(isDirty || !savedRole) && (
                            <Button variant="outlined" color="secondary" onClick={handleCancel} disabled={isSaving}>
                                <Localized id="cancel">Anuluj</Localized>
                            </Button>
                        )}
                        <Box flex={1} />
                        {savedRole && !savedRole.isDefault && (
                            <Button variant="outlined" startIcon={<StarIcon />} onClick={handleSetDefault} disabled={isDirty}>
                                <Localized id="company-roles-set-default">_Ustaw jako domyślną</Localized>
                            </Button>
                        )}
                        {savedRole && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => setConfirmDelete(true)}
                                disabled={!canDelete}
                                title={canDelete ? undefined : l10n.getString(
                                    savedRole.isDefault ? "error-company-role-is-default" : "error-company-role-in-use",
                                    null, "")}
                            >
                                <Localized id="delete">Usuń</Localized>
                            </Button>
                        )}
                    </Stack>
                </Paper>
            )}

            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
                <DialogTitle><Localized id="company-roles-delete-title">_Usunąć rolę?</Localized></DialogTitle>
                <DialogContent>
                    <Typography>{draft?.name}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)}><Localized id="cancel">Anuluj</Localized></Button>
                    <Button color="error" onClick={handleDelete}><Localized id="delete">Usuń</Localized></Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default CompanyRoles
